import Page from '../components/Page'
import Section from '../components/Section'
import Paragraphs from '../components/Paragraphs'
import PrimaryTitle from '../components/PrimaryTitle'
import LargeCard from '../components/LargeCard'
import SmallCard from '../components/SmallCard'
import paths from '../path'

const pageTitle = 'Therapeutische Wirksamkeit von VR-Therapie'

const title = 'Evidenz'

const hero = {
  paragraphs: [
    'Die S3-Leitlinie der Arbeitsgemeinschaft der Wissenschaftlichen Medizinischen Fachgesellschaften (AWMF) empfiehlt für die Behandlung von Angststörungen mittlerweile explizit den Einsatz von Virtual-Reality-Therapie. Im Rahmen von Angststörungen sind Wirksamkeit und Praxisnutzen gut zu belegen. Dabei bestimmt der Grad des Eintauchens in die virtuelle Welt insbesondere bei Angstpatienten darüber, wie stark die ausgelöste Angstsymptomatik ist. Mit anderen Worten: Eine hochqualitative Simulation ist wichtig, um zuverlässig therapeutisch wirksame VR-T Erfahrungen zu erzeugen.',
    'Bei aller Euphorie muss man im Dschungel der Anwendungsideen und virtuellen Therapieansätze dennoch kritisch an das Thema herangehen. Es handelt sich noch immer um einen sehr jungen Ansatz in einem hochkomplexen Bereich. In der Forschung haben einzelne Ansätze auch das Versprechen von VR-T insbesondere gegenüber Patienten nicht halten können.',
    'Am spannendsten ist in unseren Augen jedoch der Aspekt, dass VR für die angewandte Psychotherapie nicht nur aufwändige Anwendungen stark vereinfachen kann, sondern auch dass deren Innovationskraft immer mehr Belege findet. Alles in allem wirft die klinische Forschung ein positives Licht auf die VR-T. Zurecht findet sie auch deshalb sowohl in der Forschung als auch in der Praxis immer mehr Anhänger.',
  ],
}

const moreInfo = {
  title: 'Mehr info',
  description:
    'Das neomento System wird im Labor, an Universitätskliniken und im stationären Setting stetig getestet und evaluiert. Wenn Sie mehr wissen möchten über unsere und allgemeine Forschungsarbeit auf dem Gebiet VR-T oder das neomento System selbst in Ihre Forschungsarbeit integrieren möchten, zögern Sie nicht, uns zu kontaktieren.',
  cta: 'Kontakt',
  ctaHref: paths.contact,
}

const literature = {
  title: 'Literaturliste und -empfehlungen',
  description:
    'Im Folgenden eine Literaturliste mit wichtigen Empfehlungen, wenn Sie sich zum Thema Virtual-Reality-Therapie informieren möchten und vielleicht sogar einen Einsatz der Technologie erwägen:',
  list: [
    'Freeman D, Reeve S, Robinson A, et al (2017) Virtual reality in the assessment, understanding, and treatment of mental health disorders. Psychol Med 47:2393–2400',
    'Wiederhold BK, Bouchard S (2014) Advances in Virtual Reality and Anxiety Disorders',
    'Ghanbarzadeh R, Ghapanchi AH, Blumenstein M, Talaei-Khoei A (2014) A decade of research on the use of three-dimensional virtual worlds in health care: a systematic literature review. J Med Internet Res 16:e47',
    'Klinger E, Bouchard S, Légeron P, et al (2005) Virtual reality therapy versus cognitive behavior therapy for social phobia: a preliminary controlled study. Cyberpsychol Behav 8:76–88',
    'Anderson PL, Price M, Edwards SM, et al (2013) Virtual reality exposure therapy for social anxiety disorder: a randomized controlled trial. J Consult Clin Psychol 81:751–760',
    'Bouchard S, Dumoulin S, Robillard G, et al (2017) Virtual reality compared with in vivo exposure in the treatment of social anxiety disorder: A three-arm randomised controlled trial. British Journal of Psychiatry 210:276–283',
    'Garcia-Palacios A, Botella C, Hoffman H, Fabregat S (2007) Comparing acceptance and refusal rates of virtual reality exposure vs. in vivo exposure by patients with specific phobias. Cyberpsychol Behav 10:722–724',
    'Alsina-Jurnet I, Gutiérrez-Maldonado J, Rangel-Gómez M-V (2011) The role of presence in the level of anxiety experienced in clinical virtual environments. Comput Human Behav 27:504–512',
    'Carl, E, et al. “Virtual Reality Exposure Therapy for Anxiety and Related Disorders: A Meta-Analysis of Randomized Controlled Trials.” Journal of Anxiety Disorders, vol. 61, Jan. 2019, pp. 27–36, doi:10.1016/j.janxdis.2018.08.003.',
    'Krzystanek, M, et al. “Tips for Effective Implementation of Virtual Reality Exposure Therapy in Phobias—A Systematic Review.” Frontiers in Psychiatry, vol. 12, Sept. 2021, p. 737351, doi:10.3389/fpsyt.2021.737351.',
  ],
}

const links = {
  title: 'Links',
  articles: [
    { title: 'Bouchard et al. (2017)', description: 'Virtual reality compared with in vivo exposure in the treatment of social anxiety disorder: A three-arm randomised controlled trial', imgSrc: 'articlePlaceholder.png', imgAlt: 'Artikel', btnTitle: 'Lesen', btnHref: 'https://doi.org/10.1192/bjp.bp.116.184234' },
    { title: 'Freeman et al. (2017)', description: 'Virtual reality in the assessment, understanding, and treatment of mental health disorders', imgSrc: 'articlePlaceholder.png', imgAlt: 'Artikel', btnTitle: 'Lesen', btnHref: 'https://doi.org/10.1017/S003329171700040X' },
    { title: 'Carl et al. (2019)', description: 'Virtual reality exposure therapy for anxiety and related disorders: A meta-analysis of randomized controlled trials', imgSrc: 'articlePlaceholder.png', imgAlt: 'Artikel', btnTitle: 'Lesen', btnHref: 'https://doi.org/10.1016/j.janxdis.2018.08.003' },
  ]
}

export default function Science() {
  return (
    <Page title={pageTitle}>
      <Section>
        <PrimaryTitle title={title} />
        <img className="w-full sm:h-96 h-60 mb-10 object-cover" src="/chariteSkyline.jpg" alt="" />
        <Paragraphs paragraphs={hero.paragraphs}></Paragraphs>
      </Section>
      <Section>
        <LargeCard
          title={moreInfo.title}
          description={moreInfo.description}
          cta={moreInfo.cta}
          ctaHref={moreInfo.ctaHref}
          imgSrc="philippWithMask.png"
          imgAlt="Dr mit Maske"
        />
      </Section>

      <Section dark>
        <PrimaryTitle title={literature.title} />
        <div className="space-y-6 max-w-4xl">
          <p>{literature.description}</p>
          <ol className="list-decimal space-y-2 ml-6">
            {literature.list.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ol>
        </div>
      </Section>

      <Section>
        <PrimaryTitle center title={links.title} />
        <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
          {
            links.articles.map((article, index) => (
              <div key={index}>
                <SmallCard withPadding title={article.title} description={article.description} btnTitle="Lesen" btnHref={article.btnHref}></SmallCard>
              </div>
            ))
          }
        </div>
      </Section>
    </Page>
  )
}
